import { useState, useEffect } from 'react';
import { getSuperAdminStats, getFranchises, exportBillingCSV } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Skeleton } from '../components/ui/skeleton';
import { Button } from '../components/ui/button';
import { Users, Building2, Bell, AlertTriangle, SendHorizonal, FileDown } from 'lucide-react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const StatCard = ({ icon: Icon, label, value, color, onClick }) => {
  const colors = {
    blue: 'bg-[#397DFF]/10 text-[#397DFF]',
    green: 'bg-[#10D183]/10 text-[#10D183]',
    yellow: 'bg-[#F9C80E]/10 text-[#F9C80E]',
    red: 'bg-red-50 text-red-500',
  };
  return (
    <motion.div
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      className={`bg-white border border-[#BFC6CC]/12 rounded-2xl p-5 ${onClick ? 'cursor-pointer hover:shadow-md' : ''} transition-shadow`}
      onClick={onClick}
    >
      <div className={`w-10 h-10 rounded-lg ${colors[color]} flex items-center justify-center mb-3`}>
        <Icon className="w-5 h-5" />
      </div>
      <p className="text-2xl font-bold text-[#191D1E]">{value}</p>
      <p className="text-sm text-[#687175] mt-0.5">{label}</p>
    </motion.div>
  );
};

const SuperAdminDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [franchises, setFranchises] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [statsRes, franchisesRes] = await Promise.all([
        getSuperAdminStats(),
        getFranchises()
      ]);
      setStats(statsRes.data);
      setFranchises(franchisesRes.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExportBilling = async () => {
    try {
      toast.info('Export en cours...');
      const response = await exportBillingCSV();
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }));
      const link = document.createElement('a');
      link.href = url;
      const now = new Date();
      link.download = `facturation_${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Récap facturation téléchargé !');
    } catch (error) {
      toast.error("Erreur lors de l'export");
    }
  };

  if (loading) {
    return (
      <div className="space-y-5">
        <Skeleton className="h-10 w-64 bg-white" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-28 bg-white" />)}
        </div>
        <Skeleton className="h-64 bg-white" />
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="superadmin-dashboard">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#191D1E]">
            Bonjour !
          </h1>
          <p className="text-[#687175] mt-1">Vue d'ensemble du réseau</p>
        </div>
        <Button onClick={handleExportBilling} className="btn-primary" data-testid="export-billing-btn">
          <FileDown className="w-4 h-4 mr-2" /> Export facturation
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Building2} label="Franchisés" value={stats?.total_franchises || 0} color="blue" onClick={() => navigate('/admin/franchises')} />
        <StatCard icon={Users} label="Clients total" value={stats?.total_clients || 0} color="green" onClick={() => navigate('/admin/all-clients')} />
        <StatCard icon={Bell} label="Alertes actives" value={stats?.active_alerts || 0} color="yellow" />
        <StatCard icon={SendHorizonal} label="Alertes à envoyer" value={stats?.pending_alerts_to_send || 0} color="red" />
      </div>

      {/* Franchise overview table */}
      <div className="bg-white border border-[#BFC6CC]/12 rounded-2xl overflow-hidden" data-testid="franchises-overview">
        <div className="px-5 py-4 border-b border-[#BFC6CC]/10 flex items-center justify-between">
          <h2 className="text-lg font-bold text-[#191D1E]">Réseau de franchisés</h2>
          <button onClick={() => navigate('/admin/franchises')} className="text-sm text-[#397DFF] hover:underline" data-testid="view-all-franchises">
            Voir tout
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="text-left text-xs font-semibold text-[#687175] uppercase tracking-wider border-b border-[#BFC6CC]/10">
                <th className="px-5 py-3">Franchisé</th>
                <th className="px-5 py-3">Agence(s)</th>
                <th className="px-5 py-3 text-center">Clients</th>
                <th className="px-5 py-3">Date création</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#BFC6CC]/10">
              {franchises.map(f => (
                <tr key={f.id} className="hover:bg-[#F5F6F9] transition-colors cursor-pointer"
                  onClick={() => navigate('/admin/franchises')} data-testid={`franchise-row-${f.id}`}>
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-[#397DFF]/10 flex items-center justify-center">
                        <Building2 className="w-4 h-4 text-[#397DFF]" />
                      </div>
                      <div>
                        <p className="font-medium text-[#191D1E]">{f.name}</p>
                        <p className="text-xs text-[#687175]">{f.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-4">
                    <p className="text-sm text-[#687175]">
                      {f.agency_names?.length > 0 ? f.agency_names.join(', ') : '—'}
                    </p>
                  </td>
                  <td className="px-5 py-4 text-center">
                    <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold ${
                      f.client_count > 0 ? 'bg-[#10D183]/10 text-[#10D183]' : 'bg-[#F5F6F9] text-[#939BA0]'
                    }`}>
                      {f.client_count}
                    </span>
                  </td>
                  <td className="px-5 py-4 text-sm text-[#687175]">
                    {f.created_at ? new Date(f.created_at).toLocaleDateString('fr-FR') : '—'}
                  </td>
                </tr>
              ))}
              {franchises.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-5 py-8 text-center text-[#687175]">
                    Aucun franchisé pour le moment
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminDashboard;
